import { useEffect, useRef } from "react";
import { AxiosError } from "axios";
import { Observable } from "rxjs";
import { ToastService } from "src/Shared/Services";

const WRITE_THROUGH_DELAY_MS = 600;

/**
 * What has been written for this step since the last time it was reported.
 *
 * Deliberately NOT "was there a write pending when you asked". Edits are committed on a 600ms
 * debounce, so by the time someone clicks the stepper their typing has almost always already been
 * saved and there is nothing left pending — reporting on the pending write alone stayed silent for
 * every realistic edit and only spoke if the click landed inside the debounce window.
 *
 * "nothing" therefore means the step genuinely wrote nothing: someone read it and moved on.
 */
export type WriteThroughResult = "nothing" | "saved" | "failed";

/**
 * Folds the results of several writers into one, for a step that owns more than a single write-through.
 * A failure anywhere outranks a success, because the step as a whole did not save cleanly.
 */
export const combineWriteResults = (
  ...results: WriteThroughResult[]
): WriteThroughResult => {
  if (results.includes("failed")) return "failed";
  return results.includes("saved") ? "saved" : "nothing";
};

/**
 * Write-through saving for a wizard step (O.10, story 5589): a field edit goes to the stored
 * onboarding shortly after it is made, so navigating away mid-step and coming back shows everything
 * typed and no button ever "commits".
 *
 * `flush` forces any pending write immediately and reports everything written since it was last
 * called — the shell calls it before validating a step, so Save & Continue can never judge the step
 * against a keystroke that has not landed yet, and can confirm the save to the user.
 */
export const useWriteThrough = <TPatch extends object>(
  send: (patch: TPatch) => Observable<any>,
) => {
  const timer = useRef<any>(null);
  const pending = useRef<TPatch | null>(null);
  // Outcomes accumulate here between reports, because most writes land on the debounce rather than
  // on the flush, and a write that already succeeded is still a write worth confirming.
  const saved = useRef<boolean>(false);
  const failed = useRef<boolean>(false);
  // The debounced write may still be with the server when the user navigates. Holding it lets a
  // flush wait for the answer instead of reporting "nothing" while the request is in the air.
  const inFlight = useRef<Promise<void> | null>(null);

  const writePending = (): Promise<void> => {
    clearTimeout(timer.current);
    const patch = pending.current;
    pending.current = null;
    if (!patch) return inFlight.current ?? Promise.resolve();

    const request = new Promise<void>((resolve) => {
      send(patch).subscribe({
        next: () => {
          saved.current = true;
          resolve();
        },
        error: (error: AxiosError) => {
          ToastService.axiosError(error);
          failed.current = true;
          // A refused patch goes back on the queue, so the next flush genuinely retries it.
          // It used to be dropped the instant it was sent: the typed values stayed on screen, the
          // next flush found nothing pending and reported "nothing", and the shell then advanced
          // the step against server state that had never received them — a field marked invalid
          // while the gate read the stale, valid value it was replacing (bug 5846). Anything typed
          // since the send wins, so a retry sends the newest values rather than resurrecting old
          // ones.
          pending.current = { ...patch, ...(pending.current || {}) } as TPatch;
          resolve();
        },
      });
    });

    inFlight.current = request;
    void request.then(() => {
      if (inFlight.current === request) inFlight.current = null;
    });
    return request;
  };

  /** Commits anything outstanding, then reports and clears what this step has written. */
  const flush = async (): Promise<WriteThroughResult> => {
    await writePending();
    const result: WriteThroughResult = failed.current
      ? "failed"
      : saved.current
        ? "saved"
        : "nothing";
    saved.current = false;
    failed.current = false;
    return result;
  };

  const schedule = (patch: TPatch) => {
    pending.current = { ...(pending.current || {}), ...patch } as TPatch;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void writePending(), WRITE_THROUGH_DELAY_MS);
  };

  // A pending write is committed on unmount, not discarded. The timer used only to be cleared, so
  // leaving a step within the debounce window - clicking the stepper straight after typing - threw
  // the last edit away, and returning to the step showed it blank. That is what made a second Xero
  // key look unsaveable and its deletion look like it reverted (bugs 5684, 5708), and it contradicts
  // the write-through promise that navigating away mid-step keeps everything typed (O.10).
  //
  // No reporting here: the shell has already flushed and confirmed before the step unmounts.
  useEffect(
    () => () => {
      void writePending();
    },
    [],
  );

  return { schedule, flush };
};
